// AdminPanel_SendMessage.jsx  (redesigned v2 — narrow single-column)
// ─────────────────────────────────────────────────────────────────────────────
// Integration in AdminPanel.js:
//   1. Import:  import { SendMessagePanel } from './AdminPanel_SendMessage';
//   2. Add tab: <button onClick={() => setActiveTab('notifications')}>🔔 Nachrichten</button>
//   3. Render:
//        {activeTab === 'notifications' && (
//          <SendMessagePanel
//            makeAuthenticatedRequest={makeAuthenticatedRequest}
//            users={users}
//            usergroups={usergroups}
//          />
//        )}
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';

const LANG_OPTIONS = [
  { code: 'de', label: 'Deutsch',   flag: '🇩🇪' },
  { code: 'en', label: 'English',   flag: '🇬🇧' },
  { code: 'ru', label: 'Русский',   flag: '🇷🇺' },
  { code: 'ar', label: 'عربي',      flag: '🇸🇦' },
  { code: 'fr', label: 'Français',  flag: '🇫🇷' },
  { code: 'es', label: 'Español',   flag: '🇪🇸' },
  { code: 'pl', label: 'Polski',    flag: '🇵🇱' },
  { code: 'ro', label: 'Română',    flag: '🇷🇴' },
  { code: 'tr', label: 'Türkçe',   flag: '🇹🇷' },
  { code: 'hr', label: 'Hrvatski',  flag: '🇭🇷' },
];

export function SendMessagePanel({ makeAuthenticatedRequest, users = [], usergroups = [] }) {
  const { currentLanguage } = useLanguage();
  // Eingabesprache = Nutzersprache, nicht änderbar
  const sourceLang = LANG_OPTIONS.find(l => l.code === currentLanguage) ? currentLanguage : 'de';
  const activeLang = LANG_OPTIONS.find(l => l.code === sourceLang);

  const [targetType, setTargetType] = useState('all');
  const [targetId,   setTargetId]   = useState('');
  const [title,      setTitle]      = useState('');
  const [body,       setBody]       = useState('');
  const [status,     setStatus]     = useState(null);
  const [errorMsg,   setErrorMsg]   = useState('');

  const canSubmit =
    title.trim().length > 0 &&
    body.trim().length  > 0 &&
    status !== 'sending' &&
    (targetType === 'all' || (targetId !== '' && targetId !== '0'));

  const handleSend = async () => {
    if (!canSubmit) return;
    setStatus('sending');
    setErrorMsg('');
    try {
      const payload = {
        title:       title.trim(),
        body:        body.trim(),
        source_lang: sourceLang,
        target_type: targetType,
      };
      if (targetType !== 'all') payload.target_id = parseInt(targetId, 10);

      const res = await makeAuthenticatedRequest('/admin/notifications/send', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });

      if (res?.status === 'ok') {
        setStatus('ok');
        setTitle('');
        setBody('');
        setTargetType('all');
        setTargetId('');
        setTimeout(() => setStatus(null), 3500);
      } else {
        throw new Error(res?.message || 'Unbekannter Fehler');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Fehler beim Senden');
    }
  };

  return (
    <div style={s.root}>

      {/* ── Empfänger ── */}
      <div style={s.block}>
        <p style={s.blockLabel}>Empfänger</p>
        <div style={s.segRow}>
          {[
            { value: 'all',   icon: '🌍', label: 'Alle' },
            { value: 'group', icon: '👥', label: 'Gruppe' },
            { value: 'user',  icon: '👤', label: 'Nutzer' },
          ].map(t => (
            <button
              key={t.value}
              type="button"
              style={targetType === t.value ? { ...s.seg, ...s.segActive } : s.seg}
              onClick={() => { setTargetType(t.value); setTargetId(''); }}
            >
              <span style={s.segIcon}>{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>

        {targetType === 'group' && (
          <select style={s.select} value={targetId} onChange={e => setTargetId(e.target.value)}>
            <option value="">– Gruppe wählen –</option>
            {usergroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        )}

        {targetType === 'user' && (
          <select style={s.select} value={targetId} onChange={e => setTargetId(e.target.value)}>
            <option value="">– Nutzer wählen –</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
      </div>

      <div style={s.divider} />

      {/* ── Titel ── */}
      <div style={s.block}>
        <div style={s.labelRow}>
          <p style={s.blockLabel}>Betreff / Titel</p>
          <span style={s.counter}>{title.length}/120</span>
        </div>
        <input
          type="text"
          style={s.input}
          placeholder={`Titel auf ${activeLang?.label}…`}
          value={title}
          onChange={e => setTitle(e.target.value)}
          maxLength={120}
        />
      </div>

      {/* ── Nachricht ── */}
      <div style={s.block}>
        <div style={s.labelRow}>
          <p style={s.blockLabel}>Nachricht</p>
          <span style={s.counter}>{body.length}/500</span>
        </div>
        <textarea
          style={s.textarea}
          placeholder={`Nachrichtentext auf ${activeLang?.label}…`}
          value={body}
          onChange={e => setBody(e.target.value)}
          maxLength={500}
          rows={4}
        />
      </div>

      {/* ── Live-Vorschau ── */}
      {(title || body) && (
        <div style={s.preview}>
          <p style={s.previewLabel}>Vorschau</p>
          <div style={s.toastCard}>
            <div style={s.toastBar} />
            <div style={s.toastInner}>
              <span style={s.toastEmoji}>📢</span>
              <div>
                <div style={s.toastTitle}>{title || '(kein Titel)'}</div>
                <div style={s.toastBody}>{body   || '(kein Text)'}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Status + Submit ── */}
      <div style={s.footer}>
        {status === 'ok'      && <span style={s.msgOk}>✓ Gesendet</span>}
        {status === 'error'   && <span style={s.msgErr}>⚠ {errorMsg}</span>}
        {status === 'sending' && <span style={s.msgWait}>⏳ Wird gesendet…</span>}

        <button
          type="button"
          style={canSubmit ? s.btn : { ...s.btn, ...s.btnOff }}
          onClick={handleSend}
          disabled={!canSubmit}
        >
          {status === 'sending' ? 'Wird gesendet…' : '📨 Senden'}
        </button>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const FONT = "'Inter', -apple-system, sans-serif";

const s = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    fontFamily: FONT,
  },

  block: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '20px 0',
  },
  blockLabel: {
    margin: 0,
    fontSize: '11px',
    fontWeight: '700',
    color: '#9CA3AF',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  counter: {
    fontSize: '11px',
    color: '#D1D5DB',
    fontFamily: FONT,
  },
  hint: {
    margin: 0,
    fontSize: '12px',
    color: '#9CA3AF',
    lineHeight: '1.5',
  },

  divider: {
    height: '1px',
    background: '#F3F4F6',
  },

  segRow: {
    display: 'flex',
    gap: '8px',
  },
  seg: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    padding: '10px 6px',
    background: '#F9FAFB',
    border: '1.5px solid #E5E7EB',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '500',
    fontFamily: FONT,
    color: '#374151',
    cursor: 'pointer',
  },
  segActive: {
    background: '#FFF7EE',
    border: '1.5px solid #FDBA74',
    color: '#C2410C',
    fontWeight: '600',
  },
  segIcon: {
    fontSize: '15px',
  },

  select: {
    appearance: 'none',
    background: `#F9FAFB url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='%236B7280' d='M5 7L0 2h10z'/%3E%3C/svg%3E") no-repeat right 14px center`,
    border: '1.5px solid #E5E7EB',
    borderRadius: '12px',
    padding: '11px 36px 11px 14px',
    fontSize: '14px',
    fontFamily: FONT,
    color: '#111827',
    outline: 'none',
    width: '100%',
    cursor: 'pointer',
  },

  input: {
    background: '#F9FAFB',
    border: '1.5px solid #E5E7EB',
    borderRadius: '12px',
    padding: '12px 14px',
    fontSize: '14px',
    fontFamily: FONT,
    color: '#111827',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  textarea: {
    background: '#F9FAFB',
    border: '1.5px solid #E5E7EB',
    borderRadius: '12px',
    padding: '12px 14px',
    fontSize: '14px',
    fontFamily: FONT,
    color: '#111827',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    resize: 'vertical',
    lineHeight: '1.6',
    minHeight: '100px',
  },

  preview: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '14px',
    background: '#F9FAFB',
    border: '1.5px solid #E5E7EB',
    borderRadius: '14px',
    marginBottom: '4px',
  },
  previewLabel: {
    margin: 0,
    fontSize: '10px',
    fontWeight: '700',
    color: '#9CA3AF',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  toastCard: {
    display: 'flex',
    background: '#fff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    border: '1px solid #F3F4F6',
  },
  toastBar: {
    width: '4px',
    flexShrink: 0,
    background: 'linear-gradient(180deg, #FF6407, #FF9A56)',
  },
  toastInner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '11px 12px',
  },
  toastEmoji: {
    fontSize: '18px',
    lineHeight: 1,
    marginTop: '1px',
    flexShrink: 0,
  },
  toastTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#111827',
    fontFamily: FONT,
    lineHeight: '1.3',
  },
  toastBody: {
    fontSize: '12px',
    color: '#6B7280',
    fontFamily: FONT,
    marginTop: '3px',
    lineHeight: '1.45',
  },

  footer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    paddingTop: '20px',
    borderTop: '1px solid #F3F4F6',
    marginTop: '8px',
  },
  msgOk:   { fontSize: '13px', color: '#059669', fontWeight: '600', fontFamily: FONT },
  msgErr:  { fontSize: '13px', color: '#DC2626', fontWeight: '600', fontFamily: FONT },
  msgWait: { fontSize: '13px', color: '#9CA3AF', fontWeight: '500', fontFamily: FONT },

  btn: {
    width: '100%',
    padding: '13px',
    background: 'linear-gradient(135deg, #FF6407 0%, #FF8C42 100%)',
    border: 'none',
    borderRadius: '12px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '700',
    fontFamily: FONT,
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(255,100,7,0.30)',
    letterSpacing: '-0.01em',
  },
  btnOff: {
    background: '#E5E7EB',
    color: '#9CA3AF',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
};
